import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Card } from '@abyss/web/ui/Card';
import { CollapseProvider } from '@abyss/web/ui/CollapseProvider';
import { Heading } from '@abyss/web/ui/Heading';
import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { getFeatureFlags } from '../../../common/Utils';
import { useLagoon } from '../../../hooks/useLagoon';
import { useNotAvailableUnknownText } from '../../../hooks/useNotAvailableUnknownText';
import { FacilityLocationsResponse } from '../../../models/FacilityDetails';
import {
  PhoneNumbers,
  ProviderDetails,
  ProviderLocation,
  ProviderType,
} from '../../../models/ProviderDetails';
import { useChipStore } from '../../../store/useChipStore';
import { ChipState } from '../../../store/useChipStore/chipStore';
import {
  getAdditionalPhone,
  getFormattedPhoneNumber,
  getPrimaryOrAfterHoursPhone,
  getPrimaryPhone,
} from '../../../utils/phone.utils';
import {
  getCurrentMember,
  getMemberCoverageTypeSitusState,
} from '../../../utils/user.utils';
import { adobeLinkTrackEvent } from '../../AdobeTagging/adobeLinkTrackEvent';
import { ReverseCoverageTypesCodes } from '../../Constants';
import { h2SmallerFontForResponsive, mobileOnly } from '../../ConstantsStyles';
import {
  CollapseCardHeaderStyle,
  CollapseSection,
} from '../../FacilityDetailsTabs/FacilityDetailsAboutSection/FacilityDetailsAboutSection.styled';
import { AfterHoursPopoverIcon } from './AfterHoursPopoverIcon';
import { EmailAndWebsiteInfo } from './AllOtherInfos/EmailAndWebsiteInfo';
import { FaxSection } from './FaxSection';
import {
  ProviderDetailIconAlignment,
  SectionHeader,
} from './ProviderLocation.style';
import {
  IconMaterialContainer,
  PhoneAllContainer,
  PhoneContainer,
  PhoneFlexContainer,
  PhoneNumberContainer,
  ProviderAllPhonesContainer,
  infoSubTextContainer,
} from './ProviderLocationsDetails.style';

type Props = {
  selectedLocation?: ProviderLocation | FacilityLocationsResponse;
  isFromVirtualProviderGroup?: boolean;
  phoneNumbers?: PhoneNumbers | null;
  providerDetails?: ProviderDetails;
  tty?: string;
};

export const PhoneNumberList = ({
  selectedLocation,
  isFromVirtualProviderGroup,
  phoneNumbers,
  providerDetails,
  tty,
}: Props) => {
  const { t } = useTranslation();
  const mobile = useMediaQuery(mobileOnly);
  const [isAfterHoursPopoverOpen, setIsAfterHoursPopoverOpen] = useState(false);
  const { coverageType } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
    }))
  );
  const featureFlags = useLagoon('feature-flags')();
  const lagoonData = useLagoon('ui-messaging')();
  const afterHoursInfoPopoverContent = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.ABOUT_AFTER_HOURS_CONTENT,
  });
  const [enableAfterHoursPhone] = getFeatureFlags(featureFlags, [
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_AFTER_HOURS_PHONE,
  ]);
  const currentMember = getCurrentMember();

  const memberSitusState: string =
    getMemberCoverageTypeSitusState(currentMember);

  const { isCnSMember } = currentMember;
  const isCNSNJPlanMember = isCnSMember && memberSitusState === 'NJ';
  const afterHoursObj = getPrimaryOrAfterHoursPhone(phoneNumbers);
  const afterHoursDisplayValue = enableAfterHoursPhone && afterHoursObj?.value;
  const isAfterHoursReal = afterHoursObj?.isAfterHours;
  const shouldShowAfterHoursInfoIcon =
    enableAfterHoursPhone && afterHoursObj && !isAfterHoursReal;
  const showAdditionalPhones =
    coverageType !== ReverseCoverageTypesCodes.VISION &&
    coverageType !== ReverseCoverageTypesCodes.DENTAL;
  const showExamPhone =
    coverageType === ReverseCoverageTypesCodes.VISION &&
    providerDetails?.providerType === ProviderType.PRACTITIONER;

  const { phone, appointment, emergency, fax } = phoneNumbers ?? {};
  const primaryPhones = getPrimaryPhone(phone, appointment);
  const additionalPhones = getAdditionalPhone(appointment);
  const phoneListDataTestId = 'provider-locations-phones-list';
  const onClick = (event, name) => {
    adobeLinkTrackEvent({
      name,
      location: `body:${phoneListDataTestId}`,
      type: 'phone',
    });
  };

  const {
    providerLocations: notAvailableOrUnknownText,
    headingStyle,
    descriptionStyle,
    isNotAvailableUnknownTextEnabled,
  } = useNotAvailableUnknownText();

  const width = {
    width: isCNSNJPlanMember
      ? 'fit-content'
      : isNotAvailableUnknownTextEnabled
      ? '180px'
      : '149.5px',
  };
  const fontSize = {
    fontSize: isNotAvailableUnknownTextEnabled ? '16px' : '18px',
  };

  return (
    <div
      data-auto-testid={phoneListDataTestId}
      data-testid={phoneListDataTestId}
    >
      <SectionHeader justify="flex-end">
        <Heading
          css={h2SmallerFontForResponsive}
          data-auto-testid="contact-title"
          data-testid="contact-title"
          display="h4"
          offset={2}
          style={
            {
              marginBottom: '0px',
            } as React.CSSProperties
          }
        >
          {t('PROVIDER_DETAILS.LOCATIONS_TAB.CONTACT')}
        </Heading>
      </SectionHeader>
      <PhoneFlexContainer role="list">
        <ProviderAllPhonesContainer
          role="listitem"
          style={{
            marginBottom: mobile && isFromVirtualProviderGroup ? '36px' : '',
          }}
        >
          <IconMaterialContainer color="$primary1" icon="call" size={18} />
          <PhoneContainer css={headingStyle}>
            <span style={headingStyle}>
              {t('PROVIDER_LOCATIONS_DETAILS.PRIMARY_PHONE')}
            </span>
            <PhoneNumberContainer style={width}>
              <React.Fragment>
                {primaryPhones ? (
                  <a
                    data-auto-testid="phone-primary"
                    data-testid="phone-primary-provider-locations-tab"
                    href={`tel:${primaryPhones}`}
                    onClick={(event) => onClick(event, 'phone primary')}
                    style={{
                      marginBottom: '36px',
                      ...fontSize,
                    }}
                  >
                    {getFormattedPhoneNumber(primaryPhones)}
                  </a>
                ) : (
                  <Text
                    css={{
                      ...infoSubTextContainer(false),
                      ...descriptionStyle,
                    }}
                    data-testid="primaryPhones-phone-provider-locations-tab"
                  >
                    {notAvailableOrUnknownText}
                  </Text>
                )}
                <CollapseSection
                  style={{
                    width: isCNSNJPlanMember ? '' : '235px',
                    marginBottom: '12px',
                  }}
                >
                  {mobile && isFromVirtualProviderGroup && (
                    <CollapseProvider>
                      <Card
                        collapse
                        css={CollapseCardHeaderStyle}
                        header={t(
                          'VIRTUAL_GROUP_PROVIDER_ABOUT.SHOW_PHONE_NUMBERS'
                        )}
                        style={{
                          border: 0,
                          marginBottom: '12px',
                          padding: 0,
                          color: '$interactive1',
                          fontSize: '16px',
                        }}
                      />
                    </CollapseProvider>
                  )}
                </CollapseSection>
              </React.Fragment>
            </PhoneNumberContainer>
          </PhoneContainer>
        </ProviderAllPhonesContainer>
        {showAdditionalPhones && (
          <ProviderDetailIconAlignment>
            <PhoneAllContainer>
              <IconMaterialContainer color="$primary1" icon="call" size={18} />
              <PhoneContainer css={headingStyle}>
                {t('PROVIDER_LOCATIONS_DETAILS.APPOINTMENT_PHONE')}
                <PhoneNumberContainer style={width}>
                  {additionalPhones ? (
                    <a
                      data-auto-testid={additionalPhones}
                      data-testid="phone-appointment-provider-locations-tab"
                      href={`tel:${additionalPhones}`}
                      style={fontSize}
                    >
                      {getFormattedPhoneNumber(additionalPhones)}
                    </a>
                  ) : (
                    <Text
                      css={{
                        ...infoSubTextContainer(false),
                        ...descriptionStyle,
                      }}
                      data-testid="phone-appointment-provider-locations-tab"
                    >
                      {notAvailableOrUnknownText}
                    </Text>
                  )}
                </PhoneNumberContainer>
              </PhoneContainer>
            </PhoneAllContainer>
          </ProviderDetailIconAlignment>
        )}
        {showAdditionalPhones && (
          <ProviderDetailIconAlignment>
            <PhoneAllContainer>
              <IconMaterialContainer color="$primary1" icon="call" size={18} />
              <PhoneContainer css={headingStyle}>
                {t('PROVIDER_LOCATIONS_DETAILS.EMERGENCY_PHONE')}
                <PhoneNumberContainer style={width}>
                  {emergency ? (
                    <a
                      data-auto-testid={emergency[0]}
                      data-testid={`${emergency[0]}-provider-locations-tab`}
                      href={`tel:${emergency[0]}`}
                      style={fontSize}
                    >
                      {getFormattedPhoneNumber(emergency[0])}
                    </a>
                  ) : (
                    <Text
                      css={{
                        ...infoSubTextContainer(false),
                        ...descriptionStyle,
                      }}
                      data-testid="emergency-phone-provider-locations-tab"
                    >
                      {notAvailableOrUnknownText}
                    </Text>
                  )}
                </PhoneNumberContainer>
              </PhoneContainer>
            </PhoneAllContainer>
          </ProviderDetailIconAlignment>
        )}
        {showExamPhone && (
          <ProviderDetailIconAlignment>
            <PhoneAllContainer>
              <IconMaterialContainer color="$primary1" icon="call" size={18} />
              <PhoneContainer css={headingStyle}>
                {t('OTHER_CARE_DETAILS.EXAM_PHONE')}
                <PhoneNumberContainer style={width}>
                  {emergency ? (
                    <a
                      data-auto-testid={emergency[0]}
                      data-testid={`${emergency[0]}-provider-locations-tab`}
                      href={`tel:${emergency[0]}`}
                      style={fontSize}
                    >
                      {getFormattedPhoneNumber(emergency[0])}
                    </a>
                  ) : (
                    <Text
                      css={{
                        ...infoSubTextContainer(false),
                        ...descriptionStyle,
                      }}
                      data-testid="exam-phone-provider-locations-tab"
                    >
                      {notAvailableOrUnknownText}
                    </Text>
                  )}
                </PhoneNumberContainer>
              </PhoneContainer>
            </PhoneAllContainer>
          </ProviderDetailIconAlignment>
        )}
        {enableAfterHoursPhone && (
          <ProviderDetailIconAlignment>
            <PhoneAllContainer>
              <IconMaterialContainer color="$primary1" icon="call" size={18} />
              <PhoneContainer
                css={headingStyle}
                data-testid="after-hours-section"
              >
                {shouldShowAfterHoursInfoIcon ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    {t('PROVIDER_LOCATIONS_DETAILS.AFTER_HOURS_PHONE')}
                    <AfterHoursPopoverIcon
                      content={afterHoursInfoPopoverContent?.message}
                      isOpen={isAfterHoursPopoverOpen}
                      setIsOpen={setIsAfterHoursPopoverOpen}
                    />
                  </span>
                ) : (
                  t('PROVIDER_LOCATIONS_DETAILS.AFTER_HOURS_PHONE')
                )}

                <PhoneNumberContainer style={width}>
                  {afterHoursDisplayValue ? (
                    <a
                      data-testid="after-hours-phone-provider-locations-tab"
                      href={`tel:${afterHoursDisplayValue}`}
                      style={fontSize}
                    >
                      {getFormattedPhoneNumber(afterHoursDisplayValue)}
                    </a>
                  ) : (
                    <Text
                      css={{
                        ...infoSubTextContainer(false),
                        ...descriptionStyle,
                      }}
                      data-testid="after-hours-phone-provider-locations-tab-not-available"
                    >
                      {notAvailableOrUnknownText}
                    </Text>
                  )}
                </PhoneNumberContainer>
              </PhoneContainer>
            </PhoneAllContainer>
          </ProviderDetailIconAlignment>
        )}
        {!isFromVirtualProviderGroup && (
          <ProviderDetailIconAlignment>
            <PhoneAllContainer>
              <IconMaterialContainer color="$primary1" icon="call" size={18} />
              <PhoneContainer css={headingStyle}>
                {t('PROVIDER_LOCATIONS_DETAILS.TTY')}
                <PhoneNumberContainer style={width}>
                  {tty ? (
                    <a
                      data-auto-testid={tty}
                      data-testid={`${tty}-provider-locations-tab`}
                      href={`tel:${tty}`}
                      onClick={(event) => onClick(event, 'TTY')}
                      style={fontSize}
                    >
                      {tty}
                    </a>
                  ) : (
                    <Text
                      css={{
                        ...infoSubTextContainer(false),
                        ...descriptionStyle,
                      }}
                      data-testid="tty-provider-locations-tab"
                    >
                      {notAvailableOrUnknownText}
                    </Text>
                  )}
                </PhoneNumberContainer>
              </PhoneContainer>
            </PhoneAllContainer>
          </ProviderDetailIconAlignment>
        )}

        {isCNSNJPlanMember && (
          <FaxSection
            descriptionStyle={descriptionStyle}
            fax={fax}
            fontSize={fontSize}
            headingStyle={headingStyle}
            notAvailableOrUnknownText={notAvailableOrUnknownText}
          />
        )}
      </PhoneFlexContainer>
      {selectedLocation && (
        <EmailAndWebsiteInfo selectedLocation={selectedLocation} />
      )}
    </div>
  );
};
